import {
  Directive,
  effect,
  inject,
  input,
  OnDestroy,
  signal,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { FeatureFlagKey } from './feature-flag-keys';
import { FeatureFlagsService } from './feature-flags.service';

function isFlagOn(value: unknown): boolean {
  return value !== false && value !== null && value !== undefined;
}

/**
 * Structural directive that renders the template only when the feature flag is truthy.
 * When the flag is false or null, the template is not created (like @if() — element is not in the DOM).
 *
 * We can't add custom @featureFlag() control-flow syntax; this *featureFlag gives the same behavior.
 *
 * @example
 * <!-- Default: single value (getValue$), waits for client/flag -->
 * <section *featureFlag="FeatureFlagKey.ENABLEFAAPP">FA-only content</section>
 *
 * <!-- React to runtime flag changes -->
 * <section *featureFlag="FeatureFlagKey.ENABLEFAAPP; listenForChanges: true">...</section>
 */
@Directive({
  selector: '[featureFlag]',
  standalone: true,
})
export class FeatureFlagDirective implements OnDestroy {
  private readonly flags = inject(FeatureFlagsService);
  private readonly vcRef = inject(ViewContainerRef);
  private readonly templateRef = inject(TemplateRef<unknown>);
  private sub: Subscription | null = null;

  /** Feature flag key (FeatureFlagKey enum or string key). Set by *featureFlag="key". */
  readonly flagKey = input.required<FeatureFlagKey | string>({
    alias: 'featureFlag',
  });

  /**
   * When true, subscribe to getValueChanges$() so the template is shown/hidden when the flag
   * changes at runtime. When false (default), use getValue$() for a single value. In *featureFlag
   * microsyntax use the short key: listenForChanges.
   */
  readonly listenForChanges = input<boolean>(false, {
    alias: 'featureFlagListenForChanges',
  });

  private readonly show = signal(false);

  constructor() {
    effect(() => {
      const key = this.flagKey();
      const listen = this.listenForChanges();

      this.sub?.unsubscribe();
      this.sub = null;

      this.sub = (listen ? this.flags.getValueChanges$(key, false) : this.flags.getValue$(key, false))
        .subscribe((value) => this.show.set(isFlagOn(value)));
    });

    effect(() => {
      this.vcRef.clear();
      if (this.show()) {
        this.vcRef.createEmbeddedView(this.templateRef);
      }
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }
}
